import axios, { AxiosError } from 'axios';
import {
  ApiKeyUpdate,
  AnonymizationConfig,
  AnonymizationLoggingConfig,
  CustomPatternCreate,
  PIITypeOperation,
  DataSummary,
  EndpointFetchResponse,
  EndpointUrlInput,
  FileUploadResponse,
  NLQuery,
  OpenAIConfig,
  QueryResult,
  SQLQuery,
} from '../types/api';
import { addClientIdToRequestConfig, getClientId } from '../utils/client_id';

// API base URL and API key from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

// Log the API base URL to verify its value
console.log('API_BASE_URL:', API_BASE_URL);
console.log('Environment variable value:', process.env.REACT_APP_API_BASE_URL);
console.log('API_KEY configured:', API_KEY ? 'Yes' : 'No');

// Create axios instance with base URL
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include client ID and API key with every request
apiClient.interceptors.request.use(
  (config) => {
    // Add client ID
    config = addClientIdToRequestConfig(config);
    
    // Add API key if configured
    if (API_KEY) {
      config.headers = config.headers || {};
      config.headers['X-API-Key'] = API_KEY;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// API error handler
const handleApiError = (error: unknown): never => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.data) {
      throw new Error(
        `API Error: ${axiosError.response.status} - ${JSON.stringify(axiosError.response.data)}`
      );
    } else if (axiosError.request) {
      throw new Error(`Network Error: No response received from API - ${axiosError.message}`);
    } else {
      throw new Error(`Request Error: ${axiosError.message}`);
    }
  }
  throw error;
};

// API endpoints
export const api = {
  // File operations
  files: {
    // Upload CSV files
    upload: async (files: File[]): Promise<FileUploadResponse> => {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      try {
        const response = await apiClient.post<FileUploadResponse>('/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            // Client ID will be added by the interceptor, but we'll add it explicitly for FormData
            'X-Client-ID': getClientId(),
            // API key will be added by the interceptor, but we'll add it explicitly for FormData
            ...(API_KEY && { 'X-API-Key': API_KEY })
          },
        });
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },

    // Fetch data from endpoint
    fetchFromEndpoint: async (url: string): Promise<EndpointFetchResponse> => {
      try {
        const response = await apiClient.post<EndpointFetchResponse>('/files/fetch-from-endpoint', { url });
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },

    // Get data summary
    getDataSummary: async (): Promise<DataSummary> => {
      try {
        const response = await apiClient.get<DataSummary>('/files/summary');
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },

    // Get schema information
    getSchemaInfo: async (): Promise<string> => {
      try {
        const response = await apiClient.get<string>('/files/schema');
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
  },

  // Query operations
  queries: {
    // Execute SQL query
    executeSql: async (query: string): Promise<QueryResult> => {
      try {
        const payload: SQLQuery = { query };
        const response = await apiClient.post<QueryResult>('/queries/sql', payload);
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },

    // Execute natural language query
    executeNl: async (query: string): Promise<QueryResult> => {
      try {
        const payload: NLQuery = { query };
        const response = await apiClient.post<QueryResult>('/queries/nl', payload);
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
  },

  // Configuration operations
  config: {
    // Get OpenAI configuration
    getOpenAiConfig: async (): Promise<OpenAIConfig> => {
      try {
        const response = await apiClient.get<OpenAIConfig>('/config/openai');
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },

    // Update OpenAI API key
    updateApiKey: async (apiKey: string): Promise<{ status: string; message: string }> => {
      try {
        const payload: ApiKeyUpdate = { api_key: apiKey };
        const response = await apiClient.put<{ status: string; message: string }>(
          '/config/openai/apikey',
          payload
        );
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },

    // Update OpenAI configuration
    updateOpenAiConfig: async (config: OpenAIConfig): Promise<OpenAIConfig> => {
      try {
        const response = await apiClient.put<OpenAIConfig>('/config/openai', config);
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
    
    // Get anonymization configuration
    getAnonymizationConfig: async (): Promise<AnonymizationConfig> => {
      try {
        const response = await apiClient.get<AnonymizationConfig>('/config/anonymization');
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
    
    // Update anonymization configuration
    updateAnonymizationConfig: async (config: AnonymizationConfig): Promise<AnonymizationConfig> => {
      try {
        const response = await apiClient.put<AnonymizationConfig>('/config/anonymization', config);
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
    
    // Toggle anonymization
    toggleAnonymization: async (enabled: boolean): Promise<{ status: string; message: string }> => {
      try {
        const response = await apiClient.post<{ status: string; message: string }>(
          '/config/anonymization/toggle',
          { enabled: enabled }
        );
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
    
    // Add custom pattern
    addCustomPattern: async (pattern: CustomPatternCreate): Promise<{ status: string; message: string }> => {
      try {
        const response = await apiClient.post<{ status: string; message: string }>(
          '/config/anonymization/custom-pattern',
          pattern
        );
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
    
    // Remove custom pattern
    removeCustomPattern: async (name: string): Promise<{ status: string; message: string }> => {
      try {
        const response = await apiClient.delete<{ status: string; message: string }>(
          `/config/anonymization/custom-pattern/${name}`
        );
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
    
    // Enable PII type
    enablePIIType: async (piiType: string): Promise<{ status: string; message: string }> => {
      try {
        const payload: PIITypeOperation = { pii_type: piiType };
        const response = await apiClient.post<{ status: string; message: string }>(
          '/config/anonymization/pii-type/enable',
          payload
        );
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
    
    // Disable PII type
    disablePIIType: async (piiType: string): Promise<{ status: string; message: string }> => {
      try {
        const payload: PIITypeOperation = { pii_type: piiType };
        const response = await apiClient.post<{ status: string; message: string }>(
          '/config/anonymization/pii-type/disable',
          payload
        );
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
    
    // Get anonymization logs
    getAnonymizationLogs: async (limit: number = 20): Promise<any[]> => {
      try {
        const response = await apiClient.get<any[]>(
          `/config/anonymization/logs?limit=${limit}`
        );
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
    
    // Clear anonymization logs
    clearAnonymizationLogs: async (): Promise<{ status: string; message: string }> => {
      try {
        const response = await apiClient.delete<{ status: string; message: string }>(
          '/config/anonymization/logs'
        );
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    }
  },

  // Health check
  health: {
    // Check API health
    check: async (): Promise<{ status: string; database: string }> => {
      try {
        const response = await apiClient.get<{ status: string; database: string }>('/health');
        return response.data;
      } catch (error) {
        return handleApiError(error);
      }
    },
  },
};
